// One entry point for every browser-side ad-platform purchase signal.
//
// Purchases are otherwise recorded server-side only (payment-analytics.ts), so
// the success screen is the only place a browser tag can see one. Each
// platform keeps its own once-per-payment latch, so calling this again for the
// same payment is safe.

import {
  trackMicrosoftAdsPurchase,
  type MicrosoftAdsPurchase,
  type PaymentStatusSnapshot,
} from "@/lib/payments/microsoft-ads-purchase";
import { trackOpenAiAdsPurchase } from "@/lib/openai-ads";

const debugEnabled = process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === "true";
const shouldTrack = process.env.NODE_ENV === "production" || debugEnabled;

export type AdPurchase = MicrosoftAdsPurchase;

/** Returns whether any platform received the purchase. */
export function trackAdPurchase(purchase: AdPurchase): boolean {
  const microsoft = trackMicrosoftAdsPurchase(purchase);
  const openai = trackOpenAiAdsPurchase(purchase);
  return microsoft || openai;
}

/**
 * For success screens reached by redirect (hosted checkout), where the URL
 * alone is not proof of payment. The status endpoint is authoritative for both
 * completion and the amount charged.
 */
export async function confirmAndTrackAdPurchase(
  paymentId: string,
): Promise<boolean> {
  if (typeof window === "undefined" || !shouldTrack || !paymentId) {
    return false;
  }

  try {
    const response = await fetch(
      `/api/payment/${encodeURIComponent(paymentId)}/status`,
      { cache: "no-store" },
    );

    if (!response.ok) {
      return false;
    }

    const snapshot = (await response.json()) as PaymentStatusSnapshot;

    if (snapshot.status !== "completed") {
      return false;
    }

    return trackAdPurchase({
      paymentId,
      amount: snapshot.amount,
      tier: snapshot.tier,
    });
  } catch {
    return false;
  }
}
